import React, { useState, useRef, useEffect } from 'react';
import { confirmRecoveryCode } from '../../../api/users';
import { identifyFullStoryUser } from '../../../utils/fullstory';
import { handleUserRedirect } from '../../../utils/userRedirect';
import { useHistory } from 'react-router-dom';
import RequestButton from '../../ui_kit/utilities/request_button';
import styles from './index.module.css';

const RECOVERY_CODE_LENGTH = 32;

const OtpRecovery = ({
  onCancel,
  onBackClick,
  title = 'Enter Recovery Code',
  subtitle = 'Enter one of your backup recovery codes to sign in',
}) => {
  const history = useHistory();
  const [recoveryCode, setRecoveryCode] = useState('');
  const inputRef = useRef();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleRecoverySubmit = async (afterAction) => {
    if (recoveryCode.length < RECOVERY_CODE_LENGTH) {
      if (afterAction) afterAction();
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await confirmRecoveryCode(recoveryCode);

      if (response.success) {
        identifyFullStoryUser(response.user);
        handleUserRedirect(response.user, history, {
          isImpersonation: false,
        });
      } else {
        setError(response.error || 'Invalid recovery code. Please try again.');
        setRecoveryCode('');
        inputRef.current?.focus();
      }
    } catch (e) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
      if (afterAction) afterAction();
    }
  };

  const handleInputChange = (e) => {
    const cleanValue = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, RECOVERY_CODE_LENGTH);
    setRecoveryCode(cleanValue);
    if (error) setError(null);
  };

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  return (
    <div className={styles.auth_page}>
      <div className={styles.auth_page_content}>
        <div className={styles.auth_card}>
          <div className={styles.auth_card_header}>
            <h1 className={styles.auth_heading}>{title}</h1>
            {subtitle && <p className={styles.auth_subtitle}>{subtitle}</p>}
          </div>
          {error && <p className={styles.auth_error}>{error}</p>}
          <div className={styles.auth_field}>
            <label className={styles.auth_field_label} htmlFor='recovery-code'>
              Recovery code
            </label>
            <div className={`${styles.auth_input_wrap} ${error ? styles.auth_input_wrap_error : ''}`}>
              <input
                ref={inputRef}
                id='recovery-code'
                type='text'
                className={`${styles.auth_input} fs-mask`}
                value={recoveryCode}
                onChange={handleInputChange}
                placeholder='Enter your 32-character recovery code'
                maxLength={RECOVERY_CODE_LENGTH}
                disabled={isLoading}
                autoComplete='off'
              />
            </div>
          </div>
          <RequestButton
            request={handleRecoverySubmit}
            disabled={isLoading || recoveryCode.length < RECOVERY_CODE_LENGTH ? 'disabled' : ''}
            classnames={styles.auth_button}
            cta={isLoading ? 'Verifying…' : 'Verify code'}
          />
          <p className={styles.code_help}>Each recovery code can only be used once.</p>
          {onBackClick && (
            <button
              type='button'
              className={`${styles.auth_link} ${styles.auth_link_center}`}
              onClick={onBackClick}
              disabled={isLoading}
            >
              Back to authenticator code
            </button>
          )}
          {onCancel && (
            <button type='button' className={styles.auth_button_outline} onClick={onCancel} disabled={isLoading}>
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OtpRecovery;
